import React, { PureComponent, ReactNode } from 'react';
import { FilterFilled, PlusOutlined, SettingFilled } from '@ant-design/icons';
import { Flex } from 'app/components/common/Flex';
import { Button, Space, Tag } from 'antd';
import { TextFilterInput } from 'app/common/input/TextFilterInput';
import './MonitoredEventToolbar.less';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import {
    MonitoredEventsSettings,
    MonitoredEventsSettingsContent,
    MonitoredEventsSettingsTitle,
} from 'app/components/dashboard/global/events/MonitoredEventsSettings';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import {
    MonitoredEventsFilters,
    MonitoredEventsFiltersContent,
    MonitoredEventsFiltersTitle,
} from 'app/components/dashboard/global/events/MonitoredEventFilters';

interface MonitoredEventToolbarProps {
    action?: ReactNode;
    disabled?: boolean;
    showTags?: boolean;
    settings?: MonitoredEventsSettings;
    onFilter?: (filters: MonitoredEventsFilters) => void;
    filters?: MonitoredEventsFilters;
    onSettings?: (settings: MonitoredEventsSettings) => void;
}

class MonitoredEventToolbar extends PureComponent<MonitoredEventToolbarProps> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();
    private filterPopoverRef = React.createRef<ClosablePopover>();

    render() {
        return (
            <Flex grow={false} className={'monitored-event-toolbar'}>
                {this.props.action}
                <Space>
                    <ConditionalRender
                        key="selected-only"
                        condition={this.props.showTags && this.props.settings && this.props.settings?.activeOnly}>
                        <Tag
                            closable
                            onClose={() => this.props.onSettings({ ...this.props.settings, activeOnly: false })}>
                            Active only
                        </Tag>
                    </ConditionalRender>
                    <ConditionalRender condition={Utils.isExists(this.props.settings)}>
                        <ClosablePopover
                            key="settings-action"
                            ref={this.settingsPopoverRef}
                            trigger="click"
                            placement="bottom"
                            title={<MonitoredEventsSettingsTitle />}
                            content={
                                <MonitoredEventsSettingsContent
                                    settings={this.props.settings}
                                    onChange={settings => {
                                        this.props.onSettings(settings);
                                        this.settingsPopoverRef.current.hide();
                                    }}
                                    onClose={() => this.settingsPopoverRef.current.hide()}
                                />
                            }>
                            <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                        </ClosablePopover>
                    </ConditionalRender>
                    <ConditionalRender condition={Utils.isExists(this.props.filters)}>
                        <ClosablePopover
                            key="filter-action"
                            ref={this.filterPopoverRef}
                            trigger="click"
                            placement="bottom"
                            title={<MonitoredEventsFiltersTitle />}
                            content={
                                <MonitoredEventsFiltersContent
                                    settings={this.props.filters}
                                    onChange={filters => {
                                        this.props.onFilter(filters);
                                        this.filterPopoverRef.current.hide();
                                    }}
                                    onClose={() => this.filterPopoverRef.current.hide()}
                                />
                            }>
                            <FilterFilled onClick={() => this.filterPopoverRef.current.show()} />
                        </ClosablePopover>
                    </ConditionalRender>
                </Space>
            </Flex>
        );
    }
}

interface NoteToolbarProps extends MonitoredEventToolbarProps {
    onAddNote: () => void;
}

export class NoteToolbar extends PureComponent<NoteToolbarProps> {
    render = () => (
        <MonitoredEventToolbar
            settings={this.props.settings}
            onSettings={this.props.onSettings}
            showTags={this.props.showTags}
            action={
                <Button icon={<PlusOutlined />} onClick={this.props.onAddNote} disabled={this.props.disabled}>
                    NEW NOTE
                </Button>
            }
        />
    );
}

interface WeatherEventToolbarProps extends MonitoredEventToolbarProps {
    onAddEvent: () => void;
    textFilter: string;
    onTextFilterChange: (textFilter: string) => void;
}

export class WeatherEventToolbar extends PureComponent<WeatherEventToolbarProps> {
    render = () => (
        <MonitoredEventToolbar
            settings={this.props.settings}
            onSettings={this.props.onSettings}
            showTags={this.props.showTags}
            filters={this.props.filters}
            onFilter={this.props.onFilter}
            action={
                <Flex style={{ alignItems: 'center' }}>
                    <Button
                        style={{ marginRight: '1em' }}
                        icon={<PlusOutlined />}
                        onClick={this.props.onAddEvent}
                        disabled={this.props.disabled}>
                        NEW EVENT
                    </Button>
                    <TextFilterInput
                        style={{ width: '8em' }}
                        value={this.props.textFilter}
                        onTextFilterChange={this.props.onTextFilterChange}
                    />
                </Flex>
            }
        />
    );
}

interface OpsInstructionToolbarProps extends MonitoredEventToolbarProps {
    textFilter: string;
    onTextFilterChange: (textFilter: string) => void;
}

export class OpsInstructionToolbar extends PureComponent<OpsInstructionToolbarProps> {
    render = () => (
        <MonitoredEventToolbar
            settings={this.props.settings}
            onSettings={this.props.onSettings}
            showTags={this.props.showTags}
            action={
                <TextFilterInput value={this.props.textFilter} onTextFilterChange={this.props.onTextFilterChange} />
            }
        />
    );
}
